import { useState, useCallback, useEffect } from 'react'
import { Modal, TextInput, MultiSelect, Group, Stack, Text, Box, ActionIcon } from '@mantine/core'
import { IconPencil, IconTrash, IconPlus, IconDeviceFloppy } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import { notifications, useCurrentUser } from 'context'
import { IconCheck, IconX } from '@tabler/icons-react'
import { Button } from 'components/Button'
import type { ManagedGroup } from 'api/arcMosaico/types'
import { listSchoolTeachers } from '../service/manageStudentsService'

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

interface Props {
  opened: boolean
  onClose: () => void
  groups: ManagedGroup[]
  onCreateGroup: (name: string, tutorIds?: string[]) => Promise<{ id: string; name: string }>
  onEditGroup: (id: string, name: string) => Promise<void>
  onDeleteGroup: (id: string) => Promise<void>
}

export function ManageGroupsModal({
  opened,
  onClose,
  groups,
  onCreateGroup,
  onEditGroup,
  onDeleteGroup,
}: Props) {
  const { t } = useArcTranslation()
  const { currentUser } = useCurrentUser()
  const isManager = currentUser?.profile_used === 'manager'

  // Sub-modal states
  const [addGroupOpened, setAddGroupOpened] = useState(false)
  const [editGroup, setEditGroup] = useState<ManagedGroup | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<ManagedGroup | null>(null)
  const [groupName, setGroupName] = useState('')
  const [saving, setSaving] = useState(false)

  // Manager-only teacher picker (AR-856)
  const [teachers, setTeachers] = useState<Array<{ value: string; label: string }>>([])
  const [teachersLoaded, setTeachersLoaded] = useState(false)
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([])
  const [teachersError, setTeachersError] = useState<string | null>(null)

  // Teachers without school management permission get 403 on the tutors index,
  // so only managers fetch the list.
  useEffect(() => {
    if (!opened || !isManager || teachersLoaded) return
    let active = true
    listSchoolTeachers()
      .then(list => {
        if (!active) return
        setTeachers(
          list
            .map(teacher => ({ value: teacher.id, label: teacher.name }))
            .sort((a, b) => naturalSort(a.label, b.label)),
        )
        setTeachersLoaded(true)
      })
      .catch(() => {
        // Non-critical: the picker stays empty and the required validation blocks the save
      })
    return () => {
      active = false
    }
  }, [opened, isManager, teachersLoaded])

  const handleOpenAdd = useCallback(() => {
    setGroupName('')
    setSelectedTeacherIds([])
    setTeachersError(null)
    setAddGroupOpened(true)
  }, [])

  const handleOpenEdit = useCallback((group: ManagedGroup) => {
    setGroupName(group.name)
    setEditGroup(group)
  }, [])

  const handleSaveAdd = useCallback(async () => {
    if (!groupName.trim()) return
    if (isManager && selectedTeacherIds.length === 0) {
      setTeachersError(t('manage_groups_modal.validation_teachers_required'))
      return
    }
    setSaving(true)
    try {
      await onCreateGroup(groupName.trim(), isManager ? selectedTeacherIds : undefined)
      setAddGroupOpened(false)
      setGroupName('')
      setSelectedTeacherIds([])
      notifications.show({
        message: t('manage_groups_modal.toast_created'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : t('manage_students.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    } finally {
      setSaving(false)
    }
  }, [groupName, isManager, selectedTeacherIds, onCreateGroup, t])

  const handleSaveEdit = useCallback(async () => {
    if (!groupName.trim() || !editGroup) return
    setSaving(true)
    try {
      await onEditGroup(editGroup.id, groupName.trim())
      setEditGroup(null)
      setGroupName('')
      notifications.show({
        message: t('manage_groups_modal.toast_updated'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : t('manage_students.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    } finally {
      setSaving(false)
    }
  }, [groupName, editGroup, onEditGroup, t])

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return
    setSaving(true)
    try {
      await onDeleteGroup(deleteConfirm.id)
      setDeleteConfirm(null)
      notifications.show({
        message: t('manage_groups_modal.toast_deleted'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : t('manage_students.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    } finally {
      setSaving(false)
    }
  }, [deleteConfirm, onDeleteGroup, t])

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={t('manage_groups_modal.title')}
        size='70%'
        centered
      >
        <Box h={4} />
        <Box
          sx={theme => ({
            border: `1px solid ${theme.colors.gray[2]}`,
            borderRadius: theme.radius.md,
            overflow: 'hidden',
          })}
        >
          {/* Header */}
          <Group
            position='apart'
            px='md'
            py='sm'
            sx={theme => ({
              backgroundColor: theme.colors.gray[0],
              borderBottom: `1px solid ${theme.colors.gray[2]}`,
            })}
          >
            <Text fw={700} size='md'>{t('manage_groups_modal.groups_header')}</Text>
            <Button
              size='sm'
              color='turquoise'
              leftIcon={<IconPlus size={16} />}
              onClick={handleOpenAdd}
            >
              {t('manage_groups_modal.add_group')}
            </Button>
          </Group>

          {/* Group list */}
          {groups.length === 0 ? (
            <Text color='dimmed' ta='center' py='xl' size='sm'>
              {t('manage_groups_modal.empty')}
            </Text>
          ) : (
            [...groups]
              .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
              .map((group, index) => (
              <Group
                key={group.id}
                position='apart'
                px='md'
                py='xs'
                sx={theme => ({
                  borderBottom: index < groups.length - 1 ? `1px solid ${theme.colors.gray[1]}` : 'none',
                })}
              >
                <Text size='sm'>{group.name}</Text>
                <Group spacing='sm'>
                  <ActionIcon
                    size='lg'
                    variant='subtle'
                    onClick={() => handleOpenEdit(group)}
                  >
                    <IconPencil size={20} />
                  </ActionIcon>
                  <ActionIcon
                    size='lg'
                    variant='subtle'
                    color='red'
                    onClick={() => setDeleteConfirm(group)}
                  >
                    <IconTrash size={20} />
                  </ActionIcon>
                </Group>
              </Group>
            ))
          )}
        </Box>
      </Modal>

      {/* Add Group sub-modal */}
      <Modal
        opened={addGroupOpened}
        onClose={() => setAddGroupOpened(false)}
        title={t('manage_groups_modal.add_group_title')}
        size='sm'
        centered
        zIndex={201}
      >
        <Stack spacing='sm'>
          <TextInput
            label={t('manage_groups_modal.group_name')}
            placeholder={t('manage_groups_modal.group_name_placeholder')}
            value={groupName}
            onChange={e => setGroupName(e.currentTarget.value)}
            required
          />
          {isManager && (
            <MultiSelect
              label={t('manage_groups_modal.teachers')}
              placeholder={t('manage_groups_modal.select_teachers')}
              data={teachers}
              value={selectedTeacherIds}
              onChange={ids => {
                setSelectedTeacherIds(ids)
                if (ids.length > 0) setTeachersError(null)
              }}
              error={teachersError}
              searchable
              withinPortal
              zIndex={202}
              required
            />
          )}
          <Group position='right' mt='md'>
            <Button variant='default' onClick={() => setAddGroupOpened(false)}>
              {t('manage_groups_modal.cancel')}
            </Button>
            <Button
              color='turquoise'
              leftIcon={<IconDeviceFloppy size={16} />}
              onClick={handleSaveAdd}
              loading={saving}
            >
              {t('manage_groups_modal.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Group sub-modal */}
      <Modal
        opened={editGroup !== null}
        onClose={() => { setEditGroup(null); setGroupName('') }}
        title={t('manage_groups_modal.edit_group_title')}
        size='sm'
        centered
        zIndex={201}
      >
        <Stack spacing='sm'>
          <TextInput
            label={t('manage_groups_modal.group_name')}
            value={groupName}
            onChange={e => setGroupName(e.currentTarget.value)}
            required
          />
          <Group position='right' mt='md'>
            <Button variant='default' onClick={() => { setEditGroup(null); setGroupName('') }}>
              {t('manage_groups_modal.cancel')}
            </Button>
            <Button
              color='turquoise'
              leftIcon={<IconDeviceFloppy size={16} />}
              onClick={handleSaveEdit}
              loading={saving}
            >
              {t('manage_groups_modal.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation sub-modal */}
      <Modal
        opened={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title={t('manage_groups_modal.delete_title')}
        size='sm'
        centered
        zIndex={201}
      >
        <Stack spacing='sm'>
          <Text size='sm'>
            {t('manage_groups_modal.delete_message', {
              name: deleteConfirm?.name || '',
            })}
          </Text>
          <Group position='right' mt='md'>
            <Button variant='default' onClick={() => setDeleteConfirm(null)}>
              {t('manage_groups_modal.cancel')}
            </Button>
            <Button
              color='red'
              onClick={handleDelete}
              loading={saving}
            >
              {t('manage_groups_modal.delete_confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
